<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { Placement } from '@floating-ui/dom';
  import type { AllowedState } from '../content/states.types';

  export interface TooltipProps {
    /** Text content (plain text) */
    text?: string;
    /** Raw HTML content (rendered via {@html ...}) */
    html?: string;
    /** Placement direction (e.g. 'top', 'bottom', 'left', 'right', 'top-start', etc.) */
    placement?: Placement;
    /** Controlled visibility (if provided, overrides auto hover/focus) */
    visible?: boolean;
    /** Whether auto hover/focus triggering is enabled (default: true) */
    triggerOnHover?: boolean;
    /** Whether tapping/touching toggles the tooltip on touch devices (default: false) */
    toggleOnTouch?: boolean;
    /** Maximum time in seconds to keep the tooltip open after a touch interaction (default: 30) */
    touchTimeout?: number;
    /** Semantic state color (e.g. 'notice', 'brand', 'warning', 'error', 'gray') */
    state?: AllowedState;
    /** Additional CSS class for the tooltip popover box */
    class?: string;
    /** Custom offset in px (default: 8) */
    offset?: number;
    /** Snippet for rich tooltip content */
    content?: Snippet;
    /** Children elements that trigger the tooltip */
    children?: Snippet;
    /** Positions against this element instead of the own trigger; without children no trigger is rendered */
    reference?: HTMLElement;
  }
</script>

<script lang="ts">
  import { computePosition, autoUpdate, flip, shift, offset as offsetMiddleware, arrow } from '@floating-ui/dom';

  let {
    text,
    html,
    placement = 'top',
    visible,
    triggerOnHover = true,
    toggleOnTouch = false,
    touchTimeout = 30,
    state: colorState,
    class: customClass = '',
    offset: offsetPx = 8,
    content,
    children,
    reference,
  }: TooltipProps = $props();

  let TriggerElement: HTMLElement | undefined = $state();
  const ReferenceElement = $derived(reference ?? TriggerElement);
  let FloatingElement: HTMLElement | undefined = $state();
  let ArrowElement: HTMLElement | undefined = $state();

  let IsHovered = $state(false);
  let IsTouchOpen = $state(false);

  const HasContent = $derived(Boolean(content || html || (text !== undefined && text.trim() !== '')));
  const IsOpen = $derived(HasContent && (visible !== undefined ? visible : (triggerOnHover && IsHovered) || IsTouchOpen));

  let CleanupAutoUpdate: (() => void) | undefined;
  let TouchStartX = 0;
  let TouchStartY = 0;
  let LastTouchTimestamp = 0;
  let TouchTimeoutTimer: ReturnType<typeof setTimeout> | undefined;

  function portal(node: HTMLElement): { destroy(): void } {
    const target = ReferenceElement?.closest('dialog[open]') ?? document.body;
    queueMicrotask(() => {
      if (node.parentNode !== target) {
        target.appendChild(node);
      }
    });
    return {
      destroy(): void {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      },
    };
  }

  function clearTouchTimeout(): void {
    if (TouchTimeoutTimer) {
      clearTimeout(TouchTimeoutTimer);
      TouchTimeoutTimer = undefined;
    }
  }

  function handleTouchStart(e: TouchEvent): void {
    if (!toggleOnTouch) return;
    const touch = e.touches.item(0);
    if (touch !== null) {
      TouchStartX = touch.clientX;
      TouchStartY = touch.clientY;
    }
  }

  function handleTouchEnd(e: TouchEvent): void {
    if (!toggleOnTouch) return;
    const touch = e.changedTouches.item(0);
    if (touch !== null) {
      const deltaX = Math.abs(touch.clientX - TouchStartX);
      const deltaY = Math.abs(touch.clientY - TouchStartY);
      if (deltaX > 10 || deltaY > 10) {
        return;
      }
    }

    LastTouchTimestamp = Date.now();

    if (IsTouchOpen) {
      IsTouchOpen = false;
      clearTouchTimeout();
    } else {
      IsTouchOpen = true;
      clearTouchTimeout();
      const seconds = touchTimeout > 0 ? touchTimeout : 30;
      TouchTimeoutTimer = setTimeout(() => {
        IsTouchOpen = false;
        TouchTimeoutTimer = undefined;
      }, seconds * 1000);
    }
  }

  $effect(() => {
    if (!IsTouchOpen) return;

    function handleOutside(e: Event): void {
      const target = e.target as Node | null;
      if (!target) return;
      if (ReferenceElement?.contains(target) || FloatingElement?.contains(target)) {
        return;
      }
      IsTouchOpen = false;
      clearTouchTimeout();
    }

    const timer = setTimeout(() => {
      document.addEventListener('touchstart', handleOutside, { passive: true });
      document.addEventListener('click', handleOutside);
    }, 10);

    return (): void => {
      clearTimeout(timer);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('click', handleOutside);
    };
  });

  $effect(() => {
    return (): void => {
      clearTouchTimeout();
    };
  });

  function updatePosition(): void {
    const RefEl = ReferenceElement;
    const FloatEl = FloatingElement;
    const ArrowEl = ArrowElement;
    if (!RefEl || !FloatEl) return;

    const middleware = [offsetMiddleware(offsetPx), flip({ fallbackAxisSideDirection: 'start', padding: 6 }), shift({ padding: 6 })];

    if (ArrowEl) {
      middleware.push(arrow({ element: ArrowEl }));
    }

    void computePosition(RefEl, FloatEl, {
      placement,
      middleware,
      strategy: 'fixed',
    }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
      FloatEl.style.left = `${x.toString()}px`;
      FloatEl.style.top = `${y.toString()}px`;

      if (ArrowEl && middlewareData.arrow) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;
        const staticSide =
          {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[finalPlacement.split('-')[0] ?? 'top'] ?? 'bottom';

        ArrowEl.style.left = arrowX !== undefined ? `${arrowX.toString()}px` : '';
        ArrowEl.style.top = arrowY !== undefined ? `${arrowY.toString()}px` : '';
        ArrowEl.style.right = '';
        ArrowEl.style.bottom = '';
        ArrowEl.style.setProperty(staticSide, '-4px');
      }
    });
  }

  $effect(() => {
    if (IsOpen && ReferenceElement && FloatingElement) {
      const target = ReferenceElement.closest('dialog[open]') ?? document.body;
      if (FloatingElement.parentNode !== target) {
        target.appendChild(FloatingElement);
      }
      updatePosition();
      CleanupAutoUpdate = autoUpdate(ReferenceElement, FloatingElement, updatePosition);
    } else {
      if (CleanupAutoUpdate) {
        CleanupAutoUpdate();
        CleanupAutoUpdate = undefined;
      }
    }

    return (): void => {
      if (CleanupAutoUpdate) {
        CleanupAutoUpdate();
        CleanupAutoUpdate = undefined;
      }
    };
  });

  function handleMouseEnter(): void {
    if (triggerOnHover) IsHovered = true;
  }

  function handleMouseLeave(): void {
    if (triggerOnHover) IsHovered = false;
  }

  function handlePointerDown(e: PointerEvent): void {
    if (Date.now() - LastTouchTimestamp < 500) {
      return;
    }
    if (e.pointerType === 'touch') {
      return;
    }
    handleDismiss();
  }

  function handleClick(): void {
    if (Date.now() - LastTouchTimestamp < 500) {
      return;
    }
    handleDismiss();
  }

  function handleDismiss(): void {
    if (visible === undefined) {
      IsHovered = false;
      if (IsTouchOpen) {
        IsTouchOpen = false;
        clearTouchTimeout();
      }
    }
  }
</script>

{#if children || !reference}
  <div
    bind:this={TriggerElement}
    class="tooltipTrigger"
    role="presentation"
    onmouseenter={handleMouseEnter}
    onmouseleave={handleMouseLeave}
    ontouchstart={handleTouchStart}
    ontouchend={handleTouchEnd}
    onpointerdown={handlePointerDown}
    onclick={handleClick}>
    {@render children?.()}
  </div>
{/if}

{#if IsOpen}
  <div use:portal bind:this={FloatingElement} class="tooltipFloating {colorState ? `tooltip--${colorState}` : ''} {customClass}" role="tooltip">
    <div class="tooltipContent">
      {#if content}
        {@render content()}
      {:else if html}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html html}
      {:else if text}
        {text}
      {/if}
    </div>
    <div bind:this={ArrowElement} class="tooltipArrow"></div>
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins';

  .tooltipTrigger {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    position: relative;
    max-width: 100%;
    line-height: 1;
  }

  .tooltipFloating {
    @include mixins.theme-svelte-tag(tooltip-bg, #ffffff, #1a202c);
    @include mixins.theme-svelte-tag(tooltip-color, var(--color-gray-900, #111827), var(--color-gray-50, #f7fafc));
    @include mixins.theme-svelte-tag(tooltip-border, rgba(0, 0, 0, 0.15), rgba(255, 255, 255, 0.18));
    @include mixins.theme-svelte-tag(
      tooltip-shadow,
      (0 4px 14px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)),
      (0 4px 14px rgba(0, 0, 0, 0.45), 0 1px 3px rgba(0, 0, 0, 0.35))
    );
    @include mixins.theme-svelte-tag(tooltip-row-border, rgba(0, 0, 0, 0.08), rgba(255, 255, 255, 0.1));
    @include mixins.theme-svelte-tag(tooltip-label-color, var(--color-gray-600, rgba(17, 24, 39, 0.7)), rgba(247, 250, 252, 0.7));

    position: fixed;
    z-index: 9999;
    max-width: 22rem;
    padding: 0.45em 0.75em;
    font-size: calc(var(--font-size) * 0.85);
    line-height: 1.35;
    border-radius: calc(var(--border-radius) / 2);
    border: 1px solid var(--layout-tooltip-border);
    background: var(--layout-tooltip-bg);
    color: var(--layout-tooltip-color);
    box-shadow: var(--layout-tooltip-shadow);
    pointer-events: none;
    user-select: none;
    animation: tooltipFadeIn 0.12s ease-out;

    &.tooltip--notice {
      border-color: var(--color-notice-dominant, #00349c);

      .tooltipArrow {
        border-color: var(--color-notice-dominant, #00349c);
      }
    }

    &.tooltip--warning {
      border-color: var(--color-warning-dominant, #b34b00);

      .tooltipArrow {
        border-color: var(--color-warning-dominant, #b34b00);
      }
    }

    &.tooltip--error {
      border-color: var(--color-error-dominant, #dc0012);

      .tooltipArrow {
        border-color: var(--color-error-dominant, #dc0012);
      }
    }

    &.tooltip--brand {
      border-color: var(--color-brand-dominant, #9bcc8f);

      .tooltipArrow {
        border-color: var(--color-brand-dominant, #9bcc8f);
      }
    }

    :global(strong) {
      font-weight: 600;
    }

    :global(.tooltipRow) {
      display: flex;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.15rem 0;

      &:not(:last-child) {
        border-bottom: 1px solid var(--layout-tooltip-row-border);
      }
    }

    :global(.tooltipLabel) {
      color: var(--layout-tooltip-label-color);
      white-space: nowrap;
    }

    :global(.tooltipValue) {
      font-weight: 500;
      color: var(--layout-tooltip-color);
      white-space: nowrap;
    }
  }

  .tooltipContent {
    position: relative;
    z-index: 1;
  }

  .tooltipArrow {
    position: absolute;
    width: 8px;
    height: 8px;
    background: var(--layout-tooltip-bg);
    transform: rotate(45deg);
    border: 1px solid var(--layout-tooltip-border);
    z-index: 0;
  }

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
</style>
